import {
    IconCalendarEvent,
    IconCalendarUser,
    IconChartBar,
    IconClipboardPlus,
    IconLayout2,
    IconRouteSquare,
    IconSettings,
    IconUserHeart,
} from '@tabler/icons-react'

/**
 * NOTE: There is a tight coupling between the base routes here and the userFilter profiles.
 * If you change any of the paths here, make sure to also update/Migrate the filter profiles accordingly.
 */

export const basePaths = {
    SURGERY_SCHEDULER: '/surgery-scheduler',
    PERIODS_OVERVIEW: '/periods-overview',
    RETRO: '/retro',
    SURGEON_SCHEDULER: '/surgeon-scheduler',
    OR_SCHEDULE: '/or-schedule',
    PATIENTS: '/patients',
    DOWNLOAD: '/download',
    MIGRATE: '/migrate',
    ADMIN_PANEL: '/admin-panel',
    INSIGHTS: '/insights',
    HOSPITAL_CONFIGURATION: '/hospital-configuration',
} as const

export const pages = {
    SURGERY_SCHEDULER: {
        title: 'Taktisk plan',
        views: {
            'surgery-scheduler': { path: basePaths.SURGERY_SCHEDULER, title: 'Kalender' },
        },
        icon: IconLayout2,
    },
    PERIODS_OVERVIEW: {
        title: 'Perioder',
        views: {
            periods: { path: basePaths.PERIODS_OVERVIEW, title: 'Perioder' },
        },
        icon: IconRouteSquare,
    },
    RETRO: {
        title: 'Retro',
        views: {
            retro: { path: basePaths.RETRO, title: 'Retro' },
        },
    },
    SURGEON_SCHEDULER: {
        title: 'Bemanning',
        views: {
            practitioners: { path: `${basePaths.SURGEON_SCHEDULER}/practitioners`, title: 'Kirurger' },
            locations: { path: `${basePaths.SURGEON_SCHEDULER}/locations`, title: 'Stuer' },
            allocations: { path: `${basePaths.SURGEON_SCHEDULER}/allocations`, title: 'Fagområde' },
        },
        icon: IconCalendarUser,
    },
    OPERATIONAL_PLANNER: {
        title: 'Operasjonsprogram',
        views: {
            day: { path: `${basePaths.OR_SCHEDULE}/day`, title: 'Dag' },
            calendar: { path: `${basePaths.OR_SCHEDULE}/calendar`, title: 'Uke' },
            month: { path: `${basePaths.OR_SCHEDULE}/month`, title: 'Måned' },
        },
        icon: IconCalendarEvent,
    },
    WAITING_LIST: {
        title: 'Pasienter',
        views: {
            unscheduled: { path: `${basePaths.PATIENTS}/unscheduled`, title: 'Venteliste' },
            scheduled: { path: `${basePaths.PATIENTS}/scheduled`, title: 'Bestilte operasjoner' },
            all: { path: `${basePaths.PATIENTS}/all`, title: 'Alle' },
            migration: { path: `${basePaths.PATIENTS}/migration`, title: 'Migreringsoversikt' },
        },
        icon: IconUserHeart,
    },
    DOWNLOAD: {
        title: 'Download',
        views: {
            download: { path: basePaths.DOWNLOAD, title: 'Download' },
        },
    },
    MIGRATE: {
        title: 'Migrate',
        views: {
            migrate: { path: basePaths.MIGRATE, title: 'Migrate' },
        },
    },
    ADMIN_PANEL: {
        title: 'Admin',
        views: {
            'environment-variables': { path: `${basePaths.ADMIN_PANEL}/environment-variables`, title: 'Environment Variables' },
            'surgery-type-groups': { path: `${basePaths.ADMIN_PANEL}/surgery-type-groups`, title: 'Surgery Type Groups' },
            'entity-editor': { path: `${basePaths.ADMIN_PANEL}/entity-editor`, title: 'Entity Editor' },
            'e2e-toggles': { path: `${basePaths.ADMIN_PANEL}/e2e-toggles`, title: 'E2E Toggles' },
            optimus: { path: `${basePaths.ADMIN_PANEL}/optimus`, title: 'Optimus' },
            'planning-note-cleanup': { path: `${basePaths.ADMIN_PANEL}/planning-note-cleanup`, title: 'Planning Note Cleanup' },
        },
        icon: IconSettings,
    },
    INSIGHTS: {
        title: 'Innsikt',
        views: {
            patientFlow: { path: `${basePaths.INSIGHTS}/patient-flow`, title: 'Historisk pasientflyt' },
            waitingList: { path: `${basePaths.INSIGHTS}/waiting-list`, title: 'Ventelister' },
            retro: { path: `${basePaths.INSIGHTS}/retro`, title: 'Retro' },
        },
        icon: IconChartBar,
    },
    HOSPITAL_CONFIGURATION: {
        title: 'Sykehusoppsett',
        views: {
            surgeons: { path: `${basePaths.HOSPITAL_CONFIGURATION}/surgeons`, title: 'Kirurger' },
            rooms: { path: `${basePaths.HOSPITAL_CONFIGURATION}/rooms`, title: 'Stuer' },
            constraints: { path: `${basePaths.HOSPITAL_CONFIGURATION}/constraints`, title: 'Utstyr' },
            statuses: { path: `${basePaths.HOSPITAL_CONFIGURATION}/statuses`, title: 'Aktiviteter' },
        },
        icon: IconClipboardPlus,
    },
} as const

export const redirects = {
    SURGERY_SCHEDULER: '/master-scheduler',
    SURGEON_SCHEDULER: basePaths.SURGEON_SCHEDULER,
    OPERATIONAL_PLANNER: basePaths.OR_SCHEDULE,
    AVAILABILITY_VIEW: `${basePaths.OR_SCHEDULE}/availability`,
    PLANNING_PERIODS: '/periods',
    WAITING_LIST: basePaths.PATIENTS,
    ADMIN_PANEL: basePaths.ADMIN_PANEL,
    INSIGHTS: basePaths.INSIGHTS,
    HOSPITAL_CONFIGURATION: basePaths.HOSPITAL_CONFIGURATION,
} as const

type Pages = typeof pages

export type PageKey = keyof Pages

type ExtractPath<V> = V extends { path: infer R } ? R : never

export type PagePaths = {
    [K in keyof Pages]: ExtractPath<Pages[K]['views'][keyof Pages[K]['views']]>
}

export type Path = PagePaths[keyof PagePaths]

export function getPageKeyByPath(path: string): keyof Pages | undefined {
    for (const [pageKey, page] of Object.entries(pages)) {
        for (const view of Object.values(page.views)) {
            if (path === view.path) {
                return pageKey as keyof Pages
            }
        }
    }
    return undefined // Return undefined if no match is found
}
